import { styled, createSlots } from '@design-systems/utils';
import React from 'react';
import makeClass from 'clsx';
import { useTheme, ThemeableElement } from '@design-systems/theme';
import { Status, Shape } from './types';
import BadgeValue from './badges/BadgeValue';
import styles from './Badge.css';

export const BadgeLabel = styled('div', styles.label);

export interface BadgeProps extends ThemeableElement<'div'> {
  /** Used to define the label read out by screen-reader when there is no visual label or label is not descriptive enough  */
  'aria-label'?: string;
  /** This prop is used for testing purposes */
  automationId?: string;
  /** A label to show next to the badge  */
  label?: React.ReactNode;
  /** The layout of the status badge. Defaults to rectangular for text */
  shape?: Shape;
  /**
   * The status for the badge to display
   *
   * @default 'info'
   */
  status?: Status;
  /** Optional value (children can also be used) */
  value?: React.ReactNode;
  /** Position of the badge relative to its parent. Default is center. */
  position?: 'left' | 'right' | 'center';
}

/**
 * A badge to display status.
 * Any supplied props that are not defined here will be provided to the root element.
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
const Badge = (props: BadgeProps) => {
  const {
    shape,
    status = 'info',
    className,
    children,
    value,
    label,
    position,
    automationId,
    ...html
  } = createSlots(props, {
    label: BadgeLabel
  });

  const hasChildren = React.Children.count(children) > 0;
  let resolvedShape = shape || 'rect';

  if (!hasChildren && !value && shape !== 'round') {
    resolvedShape = 'dot';
  }

  const { themeClass } = useTheme(props, styles);

  let positionClass;
  if (position === 'right') {
    positionClass = styles['right-align'];
  } else if (position === 'left') {
    positionClass = styles['left-align'];
  }

  let content;
  if (value) {
    content = value;
  } else {
    content = <BadgeValue className={themeClass}>{children}</BadgeValue>;
  }

  return (
    <div
      {...html}
      data-automation-id={automationId}
      data-testid={automationId}
      className={makeClass(
        'idsTSBadge',
        className,
        themeClass,
        styles.badge,
        styles[status],
        styles[resolvedShape],
        positionClass
      )}
    >
      {content}
      {label}
    </div>
  );
};

/** The label to apply to a status badge */
Badge.Label = BadgeLabel;

export default Badge;
